import type { CrosswordData } from './crossword.js';
import { random } from './rng.js';

export type SelectResult =
  | { action: 'none' }
  | { action: 'select'; index: number }
  | { action: 'deselect'; index: number }
  | { action: 'swap'; from: number; to: number; solved: boolean };

export type HintResult = { from: number; to: number; solved: boolean } | null;

export interface GameState {
  gridW: number;
  gridH: number;
  total: number;
  activeCells: number[];
  tileAt: number[];
  colors: number[];
  locked: Set<number>;
  selectedIndex: number | null;
  /** Ring-marked, and immovable with it. */
  isAnchor(index: number): boolean;
  /** Pre-filled and immovable, but not ringed — an edges board's interior. */
  isPreset(index: number): boolean;
  /** Immovable for any reason: anchored, pre-filled, or locked by a hint. */
  isPinned(index: number): boolean;
  isLocked(index: number): boolean;
  isSolved(): boolean;
  select(index: number): SelectResult;
  swap(from: number, to: number): SelectResult;
  clearSelection(): void;
  hint(): HintResult;
}

export function createGame(
  { gridW, gridH, activeCells, colors, anchors, preset }: CrosswordData,
): GameState {
  const total  = activeCells.length;
  const locked = new Set<number>();
  // Anchors and pre-filled cells differ only in how they're drawn; to the
  // rules they're the same thing — a tile that starts right and stays put.
  const pinned = preset ? new Set([...anchors, ...preset]) : anchors;

  // Shuffle the tiles that are actually in play
  const nonAnchors: number[] = [];
  for (let i = 0; i < total; i++) {
    if (!pinned.has(i)) nonAnchors.push(i);
  }
  const shuffled = [...nonAnchors];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  const tileAt = new Array<number>(total);
  for (let i = 0; i < total; i++) {
    if (pinned.has(i)) tileAt[i] = i;
  }
  let pi = 0;
  for (let i = 0; i < total; i++) {
    if (!pinned.has(i)) tileAt[i] = shuffled[pi++];
  }

  return {
    gridW, gridH, total, activeCells, tileAt, colors, locked,
    selectedIndex: null,

    isAnchor(i: number) { return anchors.has(i); },
    isPreset(i: number) { return preset?.has(i) ?? false; },
    isPinned(i: number) { return pinned.has(i) || locked.has(i); },
    isLocked(i: number) { return locked.has(i); },

    isSolved() { return tileAt.every((ci, i) => ci === i); },

    select(index: number): SelectResult {
      if (pinned.has(index) || locked.has(index)) return { action: 'none' };

      if (this.selectedIndex === null) {
        this.selectedIndex = index;
        return { action: 'select', index };
      }
      if (this.selectedIndex === index) {
        this.selectedIndex = null;
        return { action: 'deselect', index };
      }

      return this.swap(this.selectedIndex, index);
    },

    /** Exchange two tiles outright — the drag-and-drop path. */
    swap(from: number, to: number): SelectResult {
      if (from === to) return { action: 'none' };
      for (const i of [from, to]) {
        if (i < 0 || i >= total || pinned.has(i) || locked.has(i)) {
          return { action: 'none' };
        }
      }
      [tileAt[from], tileAt[to]] = [tileAt[to], tileAt[from]];
      this.selectedIndex = null;
      return { action: 'swap', from, to, solved: this.isSolved() };
    },

    clearSelection() { this.selectedIndex = null; },

    hint(): HintResult {
      this.selectedIndex = null;

      const candidates: number[] = [];
      for (let i = 0; i < total; i++) {
        if (!pinned.has(i) && !locked.has(i) && tileAt[i] !== i) candidates.push(i);
      }
      if (candidates.length === 0) return null;

      const fromPos  = candidates[Math.floor(random() * candidates.length)];
      const colorIdx = tileAt[fromPos];
      const toPos    = colorIdx;

      [tileAt[fromPos], tileAt[toPos]] = [tileAt[toPos], tileAt[fromPos]];
      locked.add(toPos);

      return { from: fromPos, to: toPos, solved: this.isSolved() };
    }
  };
}
